import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { addRule, failureText, inbox, removeRule, type Rule, session, type ServerInfo, type Suggestion, type User } from './api'
import { Compose } from './compose'
import { Connect } from './connect'
import { InboxList, Sidebar, useRows, type Tab } from './inbox'
import { afterSignIn, SignIn, Settings } from './settings'
import { applyTheme, rememberOpen, setPrefs, store, useMail, usePrefs, type Conversation } from './store'
import { Teach } from './teach'
import { cancelPending } from './outbox'
import { themeList } from './themes'
import { prefetchConversation } from './thread'
import { Spinner, useCollapsed, usePullToRefresh } from './phone'
import { RefreshPanel, useRefresher } from './refresh'
import { clearSearch, searchState, setAskEnabled, useSearch } from './search'
import { SearchBox } from './search-box'
import { Glyph, press, Kbd } from './glyphs'
import { setAutocomplete } from './ghost'
import { Tune } from './tune'
import { Thread, type ReplyMode } from './thread'

type View = { name: 'inbox' } | { name: 'thread'; key: string } | { name: 'compose' } | { name: 'settings' } | { name: 'tune' }
interface Toast { text: string; undo?: () => Promise<unknown>; until: number; leaving?: boolean; action?: string }

function useMedia(query: string) {
  const [match, setMatch] = useState(() => matchMedia(query).matches)
  useEffect(() => { const list = matchMedia(query); const on = () => setMatch(list.matches); list.addEventListener('change', on); return () => list.removeEventListener('change', on) }, [query])
  return match
}

/** URL ↔ view, so a reload lands where you were. */
function fromPath(path: string): { view: View; tab: Tab } {
  const thread = /^\/c\/(.+)$/.exec(path)
  if (thread) return { view: { name: 'thread', key: decodeURIComponent(thread[1]!) }, tab: 'important' }
  if (path === '/other') return { view: { name: 'inbox' }, tab: 'other' }
  if (path === '/settings') return { view: { name: 'settings' }, tab: 'important' }
  if (path === '/compose') return { view: { name: 'compose' }, tab: 'important' }
  if (path === '/tune') return { view: { name: 'tune' }, tab: 'important' }
  return { view: { name: 'inbox' }, tab: 'important' }
}
function toPath(view: View, tab: Tab): string {
  if (view.name === 'thread') return `/c/${encodeURIComponent(view.key)}`
  if (view.name === 'inbox') return tab === 'other' ? '/other' : '/'
  return `/${view.name}`
}

const typing = (target: EventTarget | null) => target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))

// Hints double as tap targets on touch screens: each one presses its key.
const KEY_EVENTS: Record<string, { key: string; shiftKey?: boolean; metaKey?: boolean }> = {
  r: { key: 'r' }, '⇧R': { key: 'R', shiftKey: true }, f: { key: 'f' }, e: { key: 'e' }, s: { key: 's' }, m: { key: 'm' }, u: { key: 'u' },
  c: { key: 'c' }, '↵': { key: 'Enter' }, esc: { key: 'Escape' }, '⇧D': { key: 'D', shiftKey: true }, '⌘,': { key: ',', metaKey: true },
  '←': { key: 'ArrowLeft' }, '→': { key: 'ArrowRight' }, '↑': { key: 'ArrowUp' }, '⌘↵': { key: 'Enter', metaKey: true },
}
function Keys({ items }: { items: Array<[string, string, boolean?]> }) {
  return <>{items.map(([key, text, primary]) => {
    const event = KEY_EVENTS[key]
    return (
      <button key={key + text} type="button" tabIndex={-1} className={`key-hint${primary ? ' hot' : ''}${event ? ' tappable' : ''}`}
        onClick={event ? () => window.dispatchEvent(new KeyboardEvent('keydown', { ...event, bubbles: true })) : undefined}>
        <Kbd k={key} />{text}
      </button>
    )
  })}</>
}

/** Important / Other with an underline that slides between them. */
function Tabs({ tab, onTab, importantCount, otherCount, sorting }: { tab: Tab; onTab: (tab: Tab) => void; importantCount: number; otherCount: number; sorting: number }) {
  const refs = useRef<Record<Tab, HTMLButtonElement | null>>({ important: null, other: null })
  const [bar, setBar] = useState<{ left: number; width: number } | null>(null)
  useEffect(() => {
    const element = refs.current[tab]
    if (element) setBar({ left: element.offsetLeft, width: element.offsetWidth })
  }, [tab, importantCount, otherCount])
  return (
    <nav className="tabs">
      <button ref={el => { refs.current.important = el }} className={`tab${tab === 'important' ? ' on' : ''}`} onClick={() => onTab('important')}>Important <span className="tab-count">{importantCount}</span><Kbd k="i" /></button>
      <button ref={el => { refs.current.other = el }} className={`tab${tab === 'other' ? ' on' : ''}`} onClick={() => onTab('other')}>Other <span className="tab-count">{otherCount}</span><Kbd k="o" /></button>
      <span className="tab-hint"><Kbd k="tab" /></span>
      {bar && <span className="tab-bar" style={{ transform: `translateX(${bar.left}px)`, width: bar.width }} />}
      {sorting > 0 && <span className="sorting"><span className="pulse" />Sorting {sorting}</span>}
    </nav>
  )
}

function MailApp({ user, server }: { user: User; server: ServerInfo }) {
  useState(() => store.hydrate(user.id))
  setAutocomplete(!!server.complete)
  setAskEnabled(!!server.askSearch)
  const mail = useMail()
  const prefs = usePrefs()
  const initial = useMemo(() => fromPath(location.pathname), [])
  const [view, setView] = useState<View>(initial.view)
  const [tab, setTab] = useState<Tab>(initial.tab)
  const [extra, setExtra] = useState<Conversation | null>(null)
  // Keep the address bar in step: opening a thread adds a history entry; switching tabs replaces it.
  const fromHistory = useRef(false)
  useEffect(() => {
    const path = toPath(view, tab)
    if (fromHistory.current) { fromHistory.current = false; return }
    if (path === location.pathname) return
    const search = location.search.includes('connection=') ? location.search : ''
    if (view.name === 'thread' || location.pathname.startsWith('/c/')) history.pushState(null, '', path + search)
    else history.replaceState(null, '', path + search)
  }, [view, tab])
  useEffect(() => {
    const onPop = () => { const next = fromPath(location.pathname); fromHistory.current = true; setView(next.view); if (next.view.name === 'inbox') setTab(next.tab) }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const toggleGroup = useCallback((label: string) => setExpanded(previous => { const next = new Set(previous); next.has(label) ? next.delete(label) : next.add(label); return next }), [])
  const [accountId, setAccountId] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [replyRequest, setReplyRequest] = useState<{ mode: ReplyMode; at: number } | null>(null)
  const [toast, setToast] = useState<Toast | null>(null)
  // Onboarding stays up (even across the Google redirect) until "Open my inbox".
  const [onboarding, setOnboarding] = useState(() => sessionStorage.getItem('mm.onboarding') === '1')
  const [teaching, setTeaching] = useState<string | null>(null)
  const [leaving, setLeaving] = useState<Set<string>>(() => new Set())
  const pendingG = useRef(0)

  const { rows, importantCount, otherCount } = useRows(mail, tab, accountId, expanded)
  // Prefetch whatever is selected (hover or j/k) once it's held for a moment, plus the next row down.
  useEffect(() => {
    if (!selected) return
    const timer = setTimeout(() => {
      const index = rows.findIndex(row => row.item.key === selected)
      for (const row of [rows[index], rows[index + 1]]) if (row) prefetchConversation(row.item)
    }, 60)
    return () => clearTimeout(timer)
  }, [selected, rows])
  const mainRef = useRef<HTMLElement | null>(null)
  const refresher = useRefresher()
  const openedKeys = useRef(new Set<string>())
  // Rules Jev just learned (applied automatically): announce each once, with Undo.
  useEffect(() => {
    const seen = new Set<string>(JSON.parse(localStorage.getItem('mm.seenLearned') || '[]'))
    const fresh = mail.learned.filter(s => !seen.has(s.id))
    if (!fresh.length) return
    localStorage.setItem('mm.seenLearned', JSON.stringify([...seen, ...fresh.map(s => s.id)].slice(-200)))
    const where = (s: Suggestion) => s.action === 'important' ? 'Important' : 'Other'
    const what = (s: Suggestion) => s.kind === 'text' ? s.label : s.pattern
    if (fresh.length === 1) {
      const s = fresh[0]!
      flash(`✦ Jev learned: ${what(s)} → ${where(s)}`, async () => { if (s.ruleId) await removeRule(s.ruleId); store.soon(50) }, 12_000)
    } else flash(`✦ Jev learned ${fresh.length} sorting rules from what you open`, async () => { setView({ name: 'settings' }) }, 12_000, 'Review')
  }, [mail.learned])
  const [mainEl, setMainEl] = useState<HTMLElement | null>(null)
  const mainCallback = useCallback((node: HTMLElement | null) => { mainRef.current = node; setMainEl(node) }, [])
  const listScroll = useRef(0)
  const lastViewName = useRef(view.name)
  useLayoutEffect(() => {
    const main = mainRef.current
    const was = lastViewName.current
    lastViewName.current = view.name
    if (!main || was === view.name) { if (main && view.name === 'thread') main.scrollTop = 0; return }
    if (view.name === 'inbox') {
      main.scrollTop = listScroll.current
      main.querySelector<HTMLElement>('.row.selected')?.scrollIntoView({ block: 'nearest' })
    } else main.scrollTop = 0
  }, [view])
  // Wide screens: iCloud-style list on the left, the conversation on the right.
  const wide = useMedia('(min-width: 1000px)')
  const phone = useMedia('(max-width: 700px)')
  // Desktop keeps its original single-pane layout; only phones get the iOS treatment.
  const split = false && wide && (view.name === 'inbox' || view.name === 'thread')
  const phoneList = phone && view.name === 'inbox'
  const pull = usePullToRefresh(mainEl, phoneList, () => Promise.race([store.refreshNow(), new Promise(resolve => setTimeout(resolve, 8_000))]))
  const collapsed = useCollapsed(mainEl, phoneList)
  const search = useSearch()
  const byKey = useMemo(() => new Map([...(search.deep ?? []).map(r => r.item), ...(search.ai ?? []), ...(search.results ?? []), ...mail.conversations].map(item => [item.key, item])), [mail.conversations, search.results, search.ai, search.deep])
  const current = view.name === 'thread' ? byKey.get(view.key) ?? (extra?.key === view.key ? extra : null) : selected ? byKey.get(selected) ?? null : null
  // Reloaded into a thread that isn't in the first page of the inbox (or is Done): fetch just that one.
  const threadKey = view.name === 'thread' ? view.key : null
  useEffect(() => {
    if (!threadKey || byKey.has(threadKey) || extra?.key === threadKey || mail.status !== 'ready') return
    let cancelled = false
    void store.fetchConversation(threadKey).then(found => { if (!cancelled && found) setExtra(found) })
    return () => { cancelled = true }
  }, [threadKey, byKey, extra, mail.status])

  // Keep a selection on screen.
  useEffect(() => {
    if (view.name !== 'inbox' && !(split && view.name === 'thread')) return
    if (view.name === 'thread') { if (selected !== view.key) setSelected(view.key); return }
    if (!selected || !rows.some(row => row.item.key === selected)) setSelected(rows[0]?.item.key ?? null)
  }, [rows, selected, view, split])

  useEffect(() => {
    if (!toast) return
    if (toast.leaving) { const gone = setTimeout(() => setToast(null), 160); return () => clearTimeout(gone) }
    const timer = setTimeout(() => setToast(previous => previous && { ...previous, leaving: true }), Math.max(0, toast.until - Date.now()))
    return () => clearTimeout(timer)
  }, [toast])

  const flash = useCallback((text: string, undo?: () => Promise<unknown>, ms = 5_000, action?: string) => setToast({ text, ...(undo ? { undo } : {}), ...(action ? { action } : {}), until: Date.now() + ms }), [])

  const open = useCallback((key: string) => {
    const item = byKey.get(key)
    setSelected(key)
    setView({ name: 'thread', key })
    setReplyRequest(null)
    if (!item) return
    rememberOpen(item)
    openedKeys.current.add(key)
    store.engage(tab === 'other' ? 'open_other' : 'open', item.lead)
    const unread = item.messages.filter(message => !message.isRead).map(message => message.id)
    if (unread.length && server.writes) {
      store.markRead(key)
      void inbox.mutate({ messageIds: unread, changes: { isRead: true }, idempotencyKey: crypto.randomUUID(), viaMailboxId: unread.length ? item.messages.find(m => !m.isRead)!.memberships[0]!.mailboxId : undefined })
        .catch(() => {})
    }
  }, [byKey, server.writes, tab])

  /** Step to the neighbouring row, in the list or while reading. */
  const step = useCallback((delta: number) => {
    const key = view.name === 'thread' ? view.key : selected
    const live = rows.filter(row => !leaving.has(row.item.key) || row.item.key === key)
    const at = live.findIndex(row => row.item.key === key)
    const next = live[Math.max(0, Math.min(live.length - 1, (at < 0 ? 0 : at + delta)))]
    if (!next) return
    if (view.name === 'thread') open(next.item.key)
    else setSelected(next.item.key)
  }, [rows, selected, view, open, leaving])

  /** Done or Snooze: local to Minimal Mail, undoable, and the next conversation takes its place. */
  const finish = useCallback(async (item: Conversation, kind: 'done' | 'snooze') => {
    store.engage(kind === 'snooze' ? 'snooze' : openedKeys.current.has(item.key) || !item.unread ? 'done' : 'done_unopened', item.lead)
    const index = rows.findIndex(row => row.item.key === item.key)
    const neighbour = rows.slice(index + 1).find(row => !leaving.has(row.item.key))?.item.key ?? rows.slice(0, index).reverse().find(row => !leaving.has(row.item.key))?.item.key ?? null
    // The row folds away while the next one takes its place.
    setLeaving(previous => new Set(previous).add(item.key))
    store.hide(item.key) // refreshes can't bring it back from now on
    setTimeout(() => { store.remove(item.key); setLeaving(previous => { const next = new Set(previous); next.delete(item.key); return next }) }, 200)
    if (view.name === 'thread') { if (neighbour) open(neighbour); else setView({ name: 'inbox' }) } else setSelected(neighbour)
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(8, 0, 0, 0)
    try {
      const receipt = await inbox.setMailboxStates({ id: crypto.randomUUID(), targets: item.raw.targets, ...(kind === 'done' ? { done: true } : { snoozedUntil: tomorrow.toISOString() }) })
      store.settle(item.key)
      if (kind === 'done') void store.recordDone(item, true)
      flash(kind === 'done' ? 'Done' : 'Snoozed until tomorrow, 8 AM', async () => { await inbox.undoMailboxStates(receipt.id); if (kind === 'done') await store.recordDone(item, false); store.settle(item.key, false) })
    } catch (error) {
      flash(failureText(error)); store.settle(item.key, false)
    }
  }, [rows, view, open, flash, leaving])

  // x: "not important". This conversation moves to Other now; Luna writes a rule for this *kind* of mail
  // (a repo's notifications, a product's alerts), and Jev applies it to everything else. ⇧X: this exact sender.
  const notImportant = useCallback(async (item: Conversation, exactSender: boolean) => {
    const from = item.lead.from
    const email = from.email.toLowerCase()
    const name = (from.name || email).replace(/\s+via\s+.+$/i, '')
    const index = rows.findIndex(row => row.item.key === item.key)
    const neighbour = rows.slice(index + 1)[0]?.item.key ?? rows.slice(0, index).reverse()[0]?.item.key ?? null
    store.fileAway(item.key, 'Not important')
    if (view.name === 'thread') { if (neighbour) open(neighbour); else setView({ name: 'inbox' }) } else setSelected(neighbour)
    flash(exactSender ? `${name} → Other` : 'Moved to Other · Jev is learning what this is…', undefined, 4_000)
    try {
      let rule: Rule
      if (exactSender) rule = (await addRule({ kind: 'sender', pattern: email, action: 'other', label: name })).rule
      else {
        const response = await fetch('/api/not-important', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mailboxId: item.lead.memberships[0]?.mailboxId, messageId: item.lead.id, messageIds: item.messages.map(m => m.id), sender: email }) })
        const body = await response.json() as { rule?: Rule; error?: { message: string } }
        if (!body.rule) throw new Error(body.error?.message ?? 'Couldn’t file that away.')
        rule = body.rule
      }
      store.addLocalRule(rule); store.soon(50)
      const what = rule.kind === 'text' ? `“${rule.pattern.length > 70 ? `${rule.pattern.slice(0, 68)}…` : rule.pattern}”` : rule.pattern
      flash(`✦ Jev will file ${what} under Other`, async () => { await removeRule(rule.id); store.removeLocalRule(rule.id); store.unfile(item.key); store.soon(50) }, 9_000)
    } catch (error) { store.unfile(item.key); flash(failureText(error)) }
  }, [rows, view, open, flash])

  const sent = useCallback((operationId: string, text: string) => {
    if (current && text !== 'Sent') store.engage('reply', current.lead)
    void inbox.policy().then(policy => {
      const seconds = policy.undoSendSeconds
      flash(seconds ? `${text} · sending in ${seconds} s` : text, seconds ? async () => { await inbox.cancel(operationId); cancelPending(operationId); flash(text === 'Sent' ? 'Sending cancelled. Your text is in Drafts.' : 'Not sent. Your reply is back in the box.') } : undefined, Math.max(4, seconds) * 1000)
    }).catch(() => flash(text))
  }, [flash, current])

  // ---- Keyboard ----
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === ',' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); setView({ name: 'settings' }); return }
      if (teaching) return
      if (typing(event.target) || event.altKey || ((event.metaKey || event.ctrlKey) && event.key !== 'Enter')) return
      const key = event.key
      if (key === 'z' && toast?.undo && !toast.action) { event.preventDefault(); const undo = toast.undo; setToast(null); void undo().catch(error => flash(failureText(error))); return }
      if (view.name === 'tune') return
      if (view.name === 'compose' || view.name === 'settings') {
        if (key === 'Escape') { event.preventDefault(); setView(view.name === 'settings' || !current ? { name: 'inbox' } : { name: 'inbox' }) }
        return
      }
      if (pendingG.current && Date.now() - pendingG.current < 1200) {
        pendingG.current = 0
        if (key === 'i') { setTab('important'); setView({ name: 'inbox' }) }
        if (key === 'o') { setTab('other'); setView({ name: 'inbox' }) }
        if (key === 's') setView({ name: 'settings' })
        if (key === 't' && server.tune) setView({ name: 'tune' })
        return
      }
      const item = current
      switch (key) {
        case 'j': case 'ArrowDown': event.preventDefault(); step(1); break
        case 'k': case 'ArrowUp': event.preventDefault(); step(-1); break
        case 'Enter': if (view.name === 'inbox' && selected) { event.preventDefault(); open(selected) } break
        case 'Escape':
          if (view.name === 'thread') { event.preventDefault(); setView({ name: 'inbox' }) }
          else if (view.name === 'inbox' && searchState().query) { event.preventDefault(); clearSearch() }
          break
        case 'e': case 'y': if (item) { event.preventDefault(); void finish(item, 'done') } break
        case 's': case 'h': if (item) { event.preventDefault(); void finish(item, 'snooze') } break
        case 'r':
          // In the list, r checks every mailbox for new mail; inside a conversation it still replies.
          if (view.name === 'inbox' && !event.metaKey && !event.ctrlKey) { event.preventDefault(); void refresher.run(); break }
          if (item) { event.preventDefault(); setTimeout(() => setReplyRequest({ mode: 'reply', at: Date.now() }), 0) }
          break
        case 'R': case 'f':
          if (item) {
            event.preventDefault()
            const mode: ReplyMode = key === 'f' ? 'forward' : 'replyAll'
            if (view.name !== 'thread') open(item.key)
            setTimeout(() => setReplyRequest({ mode, at: Date.now() }), 0)
          }
          break
        case 'c': event.preventDefault(); setView({ name: 'compose' }); break
        case 'm': if (item) { event.preventDefault(); setTeaching(item.key) } break
        case 'x': case 'X': if (item && (view.name === 'inbox' || view.name === 'thread')) { event.preventDefault(); void notImportant(item, key === 'X') } break
        case 'Tab': if (view.name === 'inbox') { event.preventDefault(); setTab(tab === 'important' ? 'other' : 'important') } break
        case 'i': if (view.name === 'inbox') setTab('important'); break
        case 'o': if (view.name === 'inbox') setTab('other'); break
        case 'T': {
          // Cycle through every theme (built-in and yours) and say which one is on.
          const all = themeList()
          const index = all.findIndex(theme => theme.id === prefs.theme)
          const next = all[(index + 1) % all.length]!
          setPrefs({ theme: next.id }); flash(`Theme: ${next.name}`, undefined, 1500)
          break
        }
        case 'D': setPrefs({ density: prefs.density === 'compact' ? 'comfortable' : 'compact' }); break
        case '\\': setPrefs({ sidebar: !prefs.sidebar }); break
        case 'g': pendingG.current = Date.now(); break
        case 'u':
          if (item && view.name === 'thread' && server.writes) {
            event.preventDefault()
            void inbox.mutate({ messageIds: [item.lead.id], changes: { isRead: false }, idempotencyKey: crypto.randomUUID(), viaMailboxId: item.lead.memberships[0]?.mailboxId })
              .then(() => { setView({ name: 'inbox' }); store.soon(100) }).catch(error => flash(failureText(error)))
          }
          break
        case '/': if (view.name === 'inbox') { event.preventDefault(); document.querySelector<HTMLInputElement>('.search-input')?.focus() } break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [view, current, selected, step, open, finish, toast, flash, prefs, mail.accounts, server.writes, teaching, tab, rows, toggleGroup, notImportant])

  // OAuth return: say so once the inbox is showing.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (mail.status === 'ready' && mail.connections.length && params.get('connection') && !onboarding) {
      flash(params.get('connection') === 'connected' ? 'Gmail connected. Your mail is on its way.' : 'Google didn’t finish connecting. Try again in Settings.', undefined, 6_000)
      history.replaceState(null, '', '/')
    }
  }, [mail.status, mail.connections.length, flash, onboarding])

  const live = mail.connections.filter(connection => connection.status !== 'disconnected')
  useEffect(() => {
    if (mail.status === 'ready' && !live.length && !onboarding) { sessionStorage.setItem('mm.onboarding', '1'); setOnboarding(true) }
  }, [mail.status, live.length, onboarding])

  if (mail.status === 'loading') return <div className="boot muted">Loading…</div>
  if (!live.length || onboarding) {
    return <div className="app"><Connect firstRun googleEmail={user.email} onDone={() => { sessionStorage.removeItem('mm.onboarding'); setOnboarding(false); store.soon(50); if (server.tune) setView({ name: 'tune' }) }} key="onboarding" /></div>
  }

  const left = view.name === 'inbox' || (split && view.name === 'thread')
    ? (
      <Tabs tab={tab} onTab={setTab} importantCount={importantCount} otherCount={otherCount} sorting={mail.triage.pending} />
    )
    : (
      <div className="crumb">
        <button className="link back" onClick={() => setView({ name: 'inbox' })}><Kbd k="esc" /><span className="back-arrow"><Glyph name="back" size={22} />{view.name === 'thread' ? (tab === 'important' ? 'Important' : 'Other') : 'Back'}</span></button>
        <span className="muted">{tab === 'important' ? 'Important' : 'Other'}</span>
        <span className="faint">/</span>
        <span>{view.name === 'thread' ? (current?.decision?.needsReply ? 'Needs reply' : current?.subject ?? '') : view.name === 'compose' ? 'New message' : view.name === 'tune' ? 'Tune' : 'Settings'}</span>
      </div>
    )

  const bottom = view.name === 'thread'
    ? <><Keys items={[['r', 'Reply', true], ['⇧R', 'Reply all'], ['f', 'Forward'], ['e', 'Done'], ['s', 'Snooze'], ['x', 'Not important'], ['m', 'Teach'], ['u', 'Unread']]} /><span className="push" /><Keys items={[['k j', 'Previous, next']]} /></>
    : view.name === 'compose'
      ? <><Keys items={[['⌘↵', 'Send', true]]} /><span className="push" /><Keys items={[['esc', 'Discard']]} /></>
      : view.name === 'tune'
        ? <><Keys items={[['←', 'Don’t need'], ['→', 'Want to see', true], ['↑', 'Back']]} /><span className="push" /><Keys items={[['esc', 'Skip']]} /></>
      : view.name === 'settings'
        ? <><span className="push" /><Keys items={[['esc', 'Close', true]]} /></>
        : <><Keys items={[['j k', 'Move'], ['↵', 'Open', true], ['r', 'Refresh'], ['e', 'Done'], ['s', 'Snooze'], ['x', 'Not important'], ['m', 'Teach'], ['c', 'Compose']]} /><span className="push" /><Keys items={[['⇧D', prefs.density === 'compact' ? 'Comfortable' : 'Compact'], ['⌘,', 'Settings']]} /></>

  const updatedText = mail.updatedAt ? (Date.now() - mail.updatedAt < 60_000 ? 'Updated just now' : `Updated ${new Date(mail.updatedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`) : ''
  const updated = updatedText
  const phoneStatus = pull.state === 'refreshing' ? 'Checking for mail…' : mail.triage.pending ? `Sorting ${mail.triage.pending}…` : updated
  const mobileBar = view.name === 'thread'
    ? <>
        <div className="glass m-capsule">
          <button className="m-tool" onClick={() => press('e')} aria-label="Done"><Glyph name="done" /></button>
          <button className="m-tool" onClick={() => press('s')} aria-label="Snooze"><Glyph name="snooze" /></button>
          <button className="m-tool" onClick={() => press('m')} aria-label="Teach"><Glyph name="teach" /></button>
          <button className="m-tool" onClick={() => press('f')} aria-label="Forward"><Glyph name="forward" /></button>
        </div>
        <button className="glass m-round" onClick={() => press('r')} aria-label="Reply"><Glyph name="reply" /></button>
      </>
    : view.name === 'compose'
      ? <><button className="m-text" onClick={() => press('Escape')}>Cancel</button><span className="m-status" /><button className="m-text strong" onClick={() => press('Enter', { metaKey: true })}>Send</button></>
      : view.name === 'tune'
        ? <><button className="m-text" onClick={() => press('ArrowLeft')}>Don’t need</button><button className="m-text" onClick={() => press('ArrowUp')}>Back</button><button className="m-text strong" onClick={() => press('ArrowRight')}>Want to see</button></>
        : view.name === 'settings'
          ? null
          : <><span className="m-tool-space" /><button className="glass m-round" onClick={() => press('c')} aria-label="New message"><Glyph name="compose" /></button></>

  return (
    <div className={`app view-${view.name}`}>
      {phoneList && (
        <>
          {/* iOS navigation bar: the small title fades in once the large one scrolls under it. */}
          <div className={`m-nav${collapsed ? ' collapsed' : ''}`}>
            <span className="m-nav-space" />
            <div className="m-nav-title"><strong>{tab === 'important' ? 'Important' : 'Other'}</strong><span>{phoneStatus}</span></div>
            <button className="glass m-round small" onClick={() => setView({ name: 'settings' })} aria-label="Settings"><Glyph name="gear" size={20} /></button>
          </div>
          <div className={`m-ptr ${pull.state}`} style={{ transform: `translateY(${Math.max(0, pull.offset - 30) / 2}px)`, opacity: collapsed ? 0 : pull.state === 'refreshing' ? 1 : pull.progress }}>
            <Spinner progress={pull.progress} spinning={pull.state === 'refreshing'} />
          </div>
        </>
      )}
      <RefreshPanel rows={refresher.rows} leaving={refresher.leaving} />
      <header className="topbar">
        {left}
        {view.name === 'thread' && (
          <div className="m-arrows">
            <button className="glass m-round small" onClick={() => press('k')} aria-label="Previous"><Glyph name="up" size={20} /></button>
            <button className="glass m-round small" onClick={() => press('j')} aria-label="Next"><Glyph name="down" size={20} /></button>
          </div>
        )}
        <div className="topbar-right">
          {(view.name === 'inbox' || split) && <SearchBox onOpenFirst={() => { const first = rows[0]; if (first) open(first.item.key) }} onFocusList={() => { const first = rows[0]; if (first) setSelected(first.item.key) }} />}
          <button className="link icon" title="Settings (⌘,)" onClick={() => setView({ name: 'settings' })}>⚙︎</button>
        </div>
      </header>
      {split ? (
        <div className="body split">
          <div className="pane-list">
            <InboxList mail={mail} tab={tab} cells onTab={setTab} onSettings={() => setView({ name: 'settings' })} onFinish={(key, kind) => { const item = byKey.get(key); if (item) void finish(item, kind) }} expanded={expanded} onToggleGroup={toggleGroup} accountId={accountId} selected={view.name === 'thread' ? view.key : selected} leaving={leaving} onSelect={key => { if (view.name !== 'thread') setSelected(key) }} onOpen={open} onLoadMore={() => store.loadMore()} />
          </div>
          <main className="main pane-read">
            {current
              ? <Thread key={current.key} item={current} mail={mail} replyRequest={view.name === 'thread' ? replyRequest : null} onSent={sent} onKeyFromFrame={event => window.dispatchEvent(new KeyboardEvent('keydown', { key: event.key, shiftKey: event.shiftKey, metaKey: event.metaKey, ctrlKey: event.ctrlKey }))} />
              : <div className="empty muted">No conversation selected</div>}
          </main>
        </div>
      ) : (
      <div className="body">
        <main className="main" ref={mainCallback} onScroll={event => { if (lastViewName.current === 'inbox') listScroll.current = event.currentTarget.scrollTop }}>
          {/* The list stays mounted behind other views, so coming back is instant: same rows, same scroll, no fade-in. */}
          <div className="list-host" hidden={view.name !== 'inbox'}><InboxList mail={mail} tab={tab} cells={phone} status={phoneStatus} onTab={setTab} onSettings={() => setView({ name: 'settings' })} onFinish={(key, kind) => { const item = byKey.get(key); if (item) void finish(item, kind) }} expanded={expanded} onToggleGroup={toggleGroup} accountId={accountId} selected={selected} leaving={leaving} onSelect={setSelected} onOpen={open} onLoadMore={() => store.loadMore()} /></div>
          {view.name === 'thread' && (current
            ? <Thread item={current} mail={mail} replyRequest={replyRequest} onSent={sent} onKeyFromFrame={event => window.dispatchEvent(new KeyboardEvent('keydown', { key: event.key, shiftKey: event.shiftKey, metaKey: event.metaKey, ctrlKey: event.ctrlKey }))} />
            : <div className="empty muted">That conversation is no longer in your inbox.</div>)}
          {view.name === 'compose' && <Compose accounts={mail.accounts} defaultAccountId={accountId} onClose={() => setView({ name: 'inbox' })} onSent={sent} />}
          {view.name === 'settings' && <Settings user={user} onTune={server.tune ? () => setView({ name: 'tune' }) : undefined} />}
          {view.name === 'tune' && <Tune onClose={() => setView({ name: 'inbox' })} onDone={text => flash(text, undefined, 6_000)} />}
        </main>
        {(view.name === 'inbox' || view.name === 'thread') && <Sidebar mail={mail} item={current} onOpenKey={key => byKey.has(key) ? open(key) : flash('That conversation isn’t in your inbox right now.')} />}
      </div>
      )}
      <footer className="bottombar">{bottom}{mobileBar && <div className="m-bar">{mobileBar}</div>}</footer>
      {teaching && byKey.get(teaching) && <Teach item={byKey.get(teaching)!} onClose={() => setTeaching(null)} onDone={(text, undo) => flash(text, undo, 8_000)} />}
      {toast && <div key={toast.until} className={`toast${toast.leaving ? ' leaving' : ''}`} role="status">{toast.text}{toast.undo && <button className="link" onClick={() => { const undo = toast.undo!; setToast(null); void undo().catch(error => flash(failureText(error))) }}>{toast.action ? <>{toast.action}</> : <><Kbd k="z" />Undo</>}</button>}</div>}
    </div>
  )
}

function Root() {
  const [state, setState] = useState<{ user: User | null; server: ServerInfo } | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    session().then(next => {
      // Already signed in and sent here by an app's OAuth request: go straight to the consent page.
      if (next.user && afterSignIn() !== '/') { location.replace(afterSignIn()); return }
      setState(next)
    }).catch(failure => setError(failureText(failure)))
  }, [])
  if (error) return <div className="boot warn">{error}</div>
  if (!state) return <div className="boot muted">Loading…</div>
  if (!state.user) return <SignIn googleSignIn={state.server.googleSignIn} signups={state.server.signups} />
  return <MailApp user={state.user} server={state.server} />
}

applyTheme()
matchMedia('(prefers-color-scheme: dark)').addEventListener('change', applyTheme)
createRoot(document.getElementById('root')!).render(<Root />)
